import styled from 'styled-components';
import {
    ELLIPSIS,
    getDefaultTextColor,
    getDefaultTextContrastColor,
    getDefaultTextSecondaryColor,
    getPrimaryBaseColor,
} from '@mews-ui/theme';
import { TokenName } from '@mewssystems/design-tokens';
import { alpha, getTextPresetCss, getToken, Minifiable } from '@optimus-web/common';
import { MEDIA_QUERY, OPACITY, SPACING, TEXT_PRESET_KEY } from '@optimus-web/local-tokens';
import { Icon } from '../Icon';

interface StepBarContainerProps {
    vertical?: boolean;
}

export const StepBarContainer = styled.ol<StepBarContainerProps>`
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;

    ${(props) =>
        !props.vertical &&
        `
        display: flex;
        align-items: center;
        justify-content: space-between;
    `}
`;

interface StepsWrapperProps {
    vertical?: boolean;
}

export const StepsWrapper = styled.div<StepsWrapperProps>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    ${(props) =>
        !props.vertical &&
        `
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
    `}
`;

export const OpenableVerticalStepperContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
`;

export const ToggleButtonElement = styled.button<Minifiable>`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border: 0;
    background: transparent;
    width: 88px;
    padding: ${(props) =>
        props.small ? `${SPACING[100]}px ${SPACING[200]}px ${SPACING[100]}px 0 ` : `17px ${SPACING[200]}px 17px 0 `};
    cursor: pointer;
    order: 1;
    color: ${getDefaultTextColor};
`;

export const StepCountElement = styled.div`
    ${getTextPresetCss(TEXT_PRESET_KEY['200m'])}
    color: ${getDefaultTextColor};
    margin-right: ${SPACING[100]}px;
`;

interface StepElementProps extends Minifiable {
    vertical?: boolean;
    clickable?: boolean;
    active?: boolean;
    completed?: boolean;
}

export const StepElement = styled.button<StepElementProps>`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding: ${SPACING[100]}px ${SPACING[200]}px;
    background: transparent;
    border: 0;
    text-align: left;
    min-width: 112px;

    ${(props) =>
        !props.small &&
        `
        padding: ${SPACING[100]}px ${SPACING[200]}px;
    `}

    ${(props) => (props.vertical ? `width: 100%;` : `max-width: 320px;`)}

    ${(props) =>
        props.clickable &&
        !props.active &&
        `
        cursor: pointer;
    `}

    ${(props) =>
        !props.active &&
        !props.clickable &&
        !props.completed &&
        `
        opacity: ${OPACITY.disabled};
    `}
`;

interface StepTextWrapperProps {
    active?: boolean;
}

export const StepTextWrapper = styled.div<StepTextWrapperProps>`
    overflow: auto;
    margin-left: ${SPACING[200]}px;

    ${MEDIA_QUERY['from-medium']} {
        display: block;
    }
`;

export const StepPrimaryTextElement = styled.div`
    ${getTextPresetCss(TEXT_PRESET_KEY['200m'])}
    color: ${getDefaultTextColor};
    ${ELLIPSIS}
`;

export const StepSecondaryTextElement = styled.div`
    ${getTextPresetCss(TEXT_PRESET_KEY['200r'])}
    color: ${getDefaultTextSecondaryColor};
    ${ELLIPSIS}
`;

interface StepNumberIconProps {
    completed?: boolean;
    active?: boolean;
    primary?: boolean;
    singleItem?: boolean;
}

export const StepIconElement = styled(Icon)<StepNumberIconProps>`
    color: ${(props) => (props.primary ? getPrimaryBaseColor(props) : getDefaultTextColor(props))};
    border-radius: 50%;
    ${(props) =>
        props.active &&
        !props.singleItem &&
        `
        background: ${alpha(getPrimaryBaseColor(props), 0.08)};
        box-shadow: 0 0 0 8px ${alpha(getPrimaryBaseColor(props), 0.08)};
    `}
`;

export const StepNumberElement = styled.span<StepNumberIconProps>`
    ${getTextPresetCss(TEXT_PRESET_KEY['200m'])}
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${(props) =>
        props.completed || props.active
            ? getPrimaryBaseColor(props)
            : getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_DEFAULT)};
    color: ${(props) =>
        props.completed || props.active ? getDefaultTextContrastColor(props) : getDefaultTextColor(props)};
    ${(props) =>
        props.active &&
        !props.singleItem &&
        `
        box-shadow: 0 0 0 8px ${alpha(getPrimaryBaseColor(props), 0.08)};
    `}
`;

interface StepLineElementProps {
    vertical?: boolean;
    primary?: boolean;
}

export const StepLineElement = styled.li<StepLineElementProps>`
    width: 1px;
    height: ${SPACING[200]}px;
    margin-left: 28px;
    background: ${(props) =>
        props.primary ? getPrimaryBaseColor(props) : alpha(getDefaultTextColor(props), OPACITY.disabled)};

    ${(props) =>
        !props.vertical &&
        `
        min-width: ${SPACING[200]}px;
        height: 1px;
        flex-grow: 1;
        margin-left: 0;
    `}
`;
